import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import { useToast } from '@shared/components/toast';
import * as ChatThreadActions from '@shared/scenes/chat/thread/store/actions';
import { useDispatch, useSelector } from '@shared/store';
import { i18n } from '@shared/util/i18n';
import { MBChatInputBar as MBChatInputBarComponent } from '../components/bar';
import * as Actions from '../store/actions';
import { MAX_CHAT_ATTACHMENTS } from '../store/reducer';

type OwnProps = Omit<MBChatInputBarComponent['props'], 'hasAttachments' | 'onPasteFiles'>;

export function MBChatInputBar({
  onChangeText,
  onSubmitEditing,
  draftId,
  isEditing,
  ...otherProps
}: OwnProps) {
  const dispatch = useDispatch();
  const toast = useToast();
  const hasAttachments = useSelector(
    state =>
      !!state.chatInput.attachmentsFromDraftId[draftId] &&
      !!state.chatInput.attachmentsFromDraftId[draftId].length,
  );

  const changeText = useCallback(
    (text: string) => {
      onChangeText(text);
      if (isEmpty(text)) {
        ChatThreadActions.throttledUpdateIsTyping.cancel();
      } else {
        ChatThreadActions.throttledUpdateIsTyping(draftId);
      }
    },
    [draftId, onChangeText],
  );

  const onSubmit = useCallback(() => {
    onSubmitEditing();
    ChatThreadActions.throttledUpdateIsTyping.cancel();
  }, [onSubmitEditing]);

  const onLimitExceeded = useCallback(
    (rejectedCount: number) => {
      toast.show({
        style: 'warning',
        message: i18n('chat.input.attachmentLimit.exceeded', {
          max: MAX_CHAT_ATTACHMENTS,
          count: rejectedCount,
        }),
        duration: 4000,
      });
    },
    [toast],
  );

  const pasteFiles = useCallback(
    (files: File[]) => {
      // Note(fant): we only add pasted files to
      // a message if we're not editing it.
      if (!isEditing) {
        dispatch(Actions.attachFiles(files, draftId, onLimitExceeded));
      }
    },
    [dispatch, draftId, isEditing, onLimitExceeded],
  );

  return (
    <MBChatInputBarComponent
      {...otherProps}
      draftId={draftId}
      isEditing={isEditing}
      hasAttachments={hasAttachments}
      onChangeText={changeText}
      onSubmitEditing={onSubmit}
      onPasteFiles={pasteFiles}
    />
  );
}
